<script setup>
import { computed, ref } from 'vue';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const store = useStore();

const since = ref(0);
const until = ref(0);
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const issueAgentReports = useMapGetter(
  'summaryReports/getIssueAgentSummaryReports'
);
const issueTypeReports = useMapGetter(
  'summaryReports/getIssueTypeSummaryReports'
);
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isAgentLoading = computed(
  () => uiFlags.value?.isFetchingIssueAgentSummaryReports
);
const isIssueLoading = computed(
  () => uiFlags.value?.isFetchingIssueTypeSummaryReports
);

const fmtNum = v =>
  v === null || v === undefined ? '--' : Number(v).toLocaleString();
const fmtTime = v => (v ? formatTime(v) : '--');

const agentRows = computed(() =>
  (issueAgentReports.value || [])
    .map(row => ({
      id: row.id,
      name: row.name || `Agent ${row.id}`,
      avatar: row.avatar_url || row.thumbnailUrl,
      issueCount: fmtNum(row.issueCount),
      resolvedIssueCount: fmtNum(row.resolvedIssueCount),
      openIssueCount: fmtNum(row.openIssueCount),
      avgIssueTime: row.avgIssueTime ? fmtTime(row.avgIssueTime) : '--',
      totalIssueTime: row.totalIssueTime ? fmtTime(row.totalIssueTime) : '--',
      rawIssueCount: Number(row.issueCount || 0),
    }))
    .sort((a, b) => b.rawIssueCount - a.rawIssueCount)
);

const issueRows = computed(() =>
  (issueTypeReports.value || [])
    .map(row => ({
      id: row.id,
      name: row.name || `Issue ${row.id}`,
      issueCount: fmtNum(row.issueCount),
      resolvedIssueCount: fmtNum(row.resolvedIssueCount),
      openIssueCount: fmtNum(row.openIssueCount),
      avgIssueTime: row.avgIssueTime ? fmtTime(row.avgIssueTime) : '--',
      totalIssueTime: row.totalIssueTime ? fmtTime(row.totalIssueTime) : '--',
      rawIssueCount: Number(row.issueCount || 0),
    }))
    .sort((a, b) => b.rawIssueCount - a.rawIssueCount)
);

const issueChartRows = computed(() => issueRows.value.slice(0, 10));
const hasIssueChartData = computed(() =>
  issueChartRows.value.some(r => r.rawIssueCount > 0)
);

const issueChartCollection = computed(() => ({
  labels: issueChartRows.value.map(r => r.name),
  datasets: [
    {
      label: 'Total issues',
      data: issueChartRows.value.map(r => r.rawIssueCount),
      backgroundColor: '#1d75e5',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
    {
      label: 'Resolved',
      data: issueChartRows.value.map(r =>
        Number(
          (issueTypeReports.value || []).find(x => x.id === r.id)
            ?.resolvedIssueCount || 0
        )
      ),
      backgroundColor: '#059669',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: true } },
  scales: {
    x: {
      ticks: { maxRotation: 0, minRotation: 0 },
      grid: { drawOnChartArea: false },
    },
    y: {
      beginAtZero: true,
      ticks: { callback: v => Number(v).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const canDownload = computed(() => Boolean(since.value && until.value));

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = token => token === latestRefreshToken.value;

const fetchReports = async () => {
  if (!since.value || !until.value) return;
  const payload = { since: since.value, until: until.value };
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [agentResult, issueTypeResult] = await Promise.allSettled([
    store.dispatch('summaryReports/fetchIssueAgentSummaryReports', payload),
    store.dispatch('summaryReports/fetchIssueTypeSummaryReports', payload),
  ]);

  if (!isLatestRefresh(refreshToken)) return;

  const issueAgentFailed =
    agentResult.status === 'rejected' || agentResult.value === false;
  const issueTypeFailed =
    issueTypeResult.status === 'rejected' || issueTypeResult.value === false;

  if (issueAgentFailed || issueTypeFailed) {
    errorMessage.value =
      'Failed to fetch issue escalations report. Please try again.';
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchReports();
};

const downloadAgentReport = () => {
  if (!canDownload.value) return;
  store.dispatch('reports/downloadIssueAgentReports', {
    from: since.value,
    to: until.value,
    fileName: generateFileName({ type: 'issue_agents', to: until.value }),
  });
};

const downloadIssueCategoryReport = () => {
  if (!canDownload.value) return;
  store.dispatch('reports/downloadIssueTypeReports', {
    from: since.value,
    to: until.value,
    fileName: generateFileName({ type: 'issue_types', to: until.value }),
  });
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="ir-page">
    <div class="ir-shell">
      <div class="ir-header">
        <div class="ir-header-left">
          <h1 class="ir-title">{{ $t('ISSUE_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="ir-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="ir-content">
        <div v-if="errorMessage" class="ir-error">
          {{ errorMessage }}
        </div>

        <!-- Agent Issues Card -->
        <section class="ir-card">
          <div class="ir-card-header">
            <div class="ir-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                />
              </svg>
              <span>{{ $t('ISSUE_REPORTS.AGENT_TABLE.TITLE') }}</span>
            </div>
            <button
              class="ir-export-btn"
              type="button"
              :disabled="!canDownload"
              @click="downloadAgentReport"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ $t('ISSUE_REPORTS.DOWNLOAD_AGENT_REPORT') }}</span>
            </button>
          </div>
          <div class="ir-table-wrap">
            <div v-if="isAgentLoading" class="ir-loading">
              <div class="ir-spinner" />
            </div>
            <table v-else class="ir-tbl">
              <thead>
                <tr>
                  <th class="ir-th ir-th--name">
                    {{ $t('ISSUE_REPORTS.COLUMNS.AGENT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.ISSUE_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.RESOLVED_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.OPEN_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.AVG_TIME') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.TOTAL_TIME') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!agentRows.length">
                  <td colspan="6" class="ir-td ir-td--empty">
                    {{ $t('ISSUE_REPORTS.NO_DATA') }}
                  </td>
                </tr>
                <tr v-for="row in agentRows" :key="row.id" class="ir-row">
                  <td class="ir-td ir-td--name">
                    <div class="ir-agent">
                      <div class="ir-avatar">
                        <img
                          v-if="row.avatar"
                          :src="row.avatar"
                          :alt="row.name"
                          class="ir-avatar-img"
                          loading="lazy"
                        />
                        <span v-else class="ir-avatar-ini">{{
                          (row.name || '?')[0]
                        }}</span>
                      </div>
                      <span class="ir-agent-name">{{ row.name }}</span>
                    </div>
                  </td>
                  <td class="ir-td ir-td--metric">{{ row.issueCount }}</td>
                  <td class="ir-td ir-td--metric">
                    <span class="ir-resolved">{{
                      row.resolvedIssueCount
                    }}</span>
                  </td>
                  <td class="ir-td ir-td--metric">
                    <span class="ir-open">{{ row.openIssueCount }}</span>
                  </td>
                  <td class="ir-td ir-td--metric">{{ row.avgIssueTime }}</td>
                  <td class="ir-td ir-td--metric">{{ row.totalIssueTime }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Issue Category Chart -->
        <section class="ir-card">
          <div class="ir-card-header">
            <div class="ir-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 3v18h18M7 14l3-3 3 2 4-5"
                />
              </svg>
              <span>Issue category breakdown</span>
            </div>
          </div>
          <div class="ir-chart-body">
            <woot-loading-state
              v-if="isIssueLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasIssueChartData" class="ir-chart-empty">
              No data available for the selected period.
            </div>
            <BarChart
              v-else
              :collection="issueChartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>

        <!-- Issue Type/Category Card -->
        <section class="ir-card">
          <div class="ir-card-header">
            <div class="ir-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                />
              </svg>
              <span>{{ $t('ISSUE_REPORTS.ISSUE_TABLE.TITLE') }}</span>
            </div>
            <button
              class="ir-export-btn"
              type="button"
              :disabled="!canDownload"
              @click="downloadIssueCategoryReport"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ $t('ISSUE_REPORTS.DOWNLOAD_ISSUE_REPORT') }}</span>
            </button>
          </div>
          <div class="ir-table-wrap">
            <div v-if="isIssueLoading" class="ir-loading">
              <div class="ir-spinner" />
            </div>
            <table v-else class="ir-tbl">
              <thead>
                <tr>
                  <th class="ir-th ir-th--name">
                    {{ $t('ISSUE_REPORTS.COLUMNS.ISSUE') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.ISSUE_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.RESOLVED_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.OPEN_COUNT') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.AVG_TIME') }}
                  </th>
                  <th class="ir-th ir-th--metric">
                    {{ $t('ISSUE_REPORTS.COLUMNS.TOTAL_TIME') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!issueRows.length">
                  <td colspan="6" class="ir-td ir-td--empty">
                    {{ $t('ISSUE_REPORTS.NO_DATA') }}
                  </td>
                </tr>
                <tr v-for="row in issueRows" :key="row.id" class="ir-row">
                  <td class="ir-td ir-td--name">
                    <span class="ir-issue-name">{{ row.name }}</span>
                  </td>
                  <td class="ir-td ir-td--metric">{{ row.issueCount }}</td>
                  <td class="ir-td ir-td--metric">
                    <span class="ir-resolved">{{
                      row.resolvedIssueCount
                    }}</span>
                  </td>
                  <td class="ir-td ir-td--metric">
                    <span class="ir-open">{{ row.openIssueCount }}</span>
                  </td>
                  <td class="ir-td ir-td--metric">{{ row.avgIssueTime }}</td>
                  <td class="ir-td ir-td--metric">{{ row.totalIssueTime }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.ir-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.ir-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.ir-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.ir-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.ir-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.ir-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.ir-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ir-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

/* ── Section cards ── */
.ir-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.ir-card-header {
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ir-card-title {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ir-export-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.ir-export-btn:hover {
  background: #f9fafb;
}
.ir-export-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Table ── */
.ir-table-wrap {
  overflow-x: auto;
}

.ir-loading {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ir-tbl {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.ir-th {
  padding: 10px 16px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.ir-th--name {
  min-width: 200px;
}
.ir-th--metric {
  min-width: 130px;
  text-align: center;
}

.ir-td {
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
  font-size: 13px;
  color: #374151;
}

.ir-td--name {
  min-width: 200px;
}
.ir-td--metric {
  min-width: 130px;
  text-align: center;
  font-weight: 600;
  color: #111827;
}
.ir-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}

.ir-row:last-child .ir-td {
  border-bottom: 0;
}
.ir-row:hover .ir-td {
  background: #fafafa;
}

.ir-agent {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ir-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ir-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ir-avatar-ini {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.ir-agent-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.ir-issue-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.ir-resolved {
  color: #059669;
  font-weight: 700;
}
.ir-open {
  color: #dc2626;
  font-weight: 700;
}

/* ── Chart ── */
.ir-chart-body {
  height: 260px;
  padding: 10px 16px 16px;
}

.ir-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

/* ── Spinner ── */
.ir-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ir-spin 0.8s linear infinite;
}

@keyframes ir-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Dark mode override ── */
::global(body.dark) .ir-page,
::global(body.dark) .ir-shell,
::global(body.dark) .ir-header,
::global(body.dark) .ir-toolbar,
::global(body.dark) .ir-card,
::global(body.theme-mix) .ir-page,
::global(body.theme-mix) .ir-shell,
::global(body.theme-mix) .ir-header,
::global(body.theme-mix) .ir-toolbar,
::global(body.theme-mix) .ir-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .ir-content {
    padding: 12px 16px 16px;
  }
  .ir-toolbar {
    padding: 10px 16px 8px;
  }
}
</style>
